import { colorStrings, mediaQueriesBP } from "@bevco/Utilities/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

export const enum fields {
    title = "title",
    description = "description",
    maxNumber = "maxNumber",
    spacingTop = "spacingTop",
    spacingBetweenCards = "spacingBetweenCards",
    spacingBottom = "spacingBottom",

    displayTitle = "Group title",
    displayDescription = "Group description",
    displayMaxNumber = "Max number to show per row",
    displaySpacingTop = "Spacing top",
    displaySpacingBetweenCards = "Spacing between cards",
    displaySpacingBottom = "Spacing bottom",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.maxNumber]: number;
        [fields.spacingTop]: number;
        [fields.spacingBetweenCards]: number;
        [fields.spacingBottom]: number;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps> & ThemeProps<BaseTheme> & HasShellContext;

export interface TeamCardGroupWidgetStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    cardWrapperEditMode?: InjectableCss;
    cardWrapper?: InjectableCss;
    dotsGroup?: InjectableCss;
}

export const teamCardGroupWidgetStyles: TeamCardGroupWidgetStyles = {
    cardWrapperEditMode: {
        css: css`
            [class*="ZoneWrapper-"] {
                display: flex;
                flex-wrap: wrap;

                [class*="AddContainer-"] {
                    width: 100%;
                }
            }
        `,
    },
};

export const styles = teamCardGroupWidgetStyles;

export const TeamCardGroupWidget = ({
    id,
    fields: { title, description, maxNumber, spacingTop, spacingBetweenCards, spacingBottom },
    shellContext: { isEditing },
    theme: { breakpoints },
}: Props) => {
    const [isMobile, setIsMobile] = useState(false);
    const [selectedIndex, setSelectedindex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<Array<number>>([]);
    const [emblaRef, embla] = useEmblaCarousel();

    const scrollTo = useCallback((index: any) => embla?.scrollTo(index), [embla]);

    const onSelect = useCallback(() => {
        if (!embla) {
            return;
        }
        setSelectedindex(embla.selectedScrollSnap());
    }, [embla, setSelectedindex]);

    useEffect(() => {
        const handleWindowSize = () => {
            const maxMobileIdx = breakpoints.keys.indexOf("md");
            const maxMobile = breakpoints.values[maxMobileIdx];
            setIsMobile(window.innerWidth < maxMobile);
        };
        handleWindowSize();
        window.addEventListener("resize", debounce(handleWindowSize, 200));
    }, []);

    useEffect(() => {
        if (!embla) {
            return;
        }
        const draggable = isTouchDevice();
        if (isMobile) {
            embla.reInit({
                align: "start",
                watchDrag: draggable,
                slidesToScroll: 2,
            });
        } else {
            embla.reInit({
                align: "start",
                watchDrag: draggable,
            });
        }

        embla.scrollTo(0);
        onSelect();
        setScrollSnaps(embla.scrollSnapList());
        embla.on("select", onSelect);
    }, [embla, isMobile, setScrollSnaps, onSelect]);

    const spacing = spacingBetweenCards * (maxNumber - 1);
    const text = `calc((100% / ${maxNumber}) - (${spacing}px / ${maxNumber}))`;
    const sizes = css`
        margin-right: ${spacingBetweenCards}px;
        max-width: ${text};
        width: 100%;

        &:nth-child(${maxNumber}n) {
            margin-right: 0;
        }

        &:nth-child(n + ${+maxNumber + 1}) {
            margin-top: ${spacingBetweenCards}px;
        }
    `;

    const cartWrapperStyle: InjectableCss = {};
    if (isEditing) {
        cartWrapperStyle.css = css`
            ${styles.cardWrapperEditMode?.css}

            [class*="ZoneWrapper-"] {
                & > *:not([class*="AddContainer-"]) {
                    ${sizes}
                }
            }
        `;
    } else {
        cartWrapperStyle.css = css`
            ${styles.cardWrapper?.css}

            ${mediaQueriesBP.minMD} {
                & > [class*="Wrapper-"] {
                    & > * {
                        ${sizes}
                    }
                }
            }
        `;
    }

    const wrapperStyle = styles.wrapper ?? {};
    wrapperStyle.css = css`
        ${wrapperStyle?.css}
        margin-top: ${spacingTop ?? 0}px;
        margin-bottom: ${spacingBottom ?? 0}px;
    `;

    return (
        <StyledWrapper {...wrapperStyle}>
            <Typography {...styles.title}>{title}</Typography>

            {description && <Typography {...styles.description}>{parse(description, parserOptions)}</Typography>}

            {isEditing && (
                <>
                    <Typography variant="legend" color={colorStrings.dangerMain}>
                        {translate("The Team Card widget is the only one optimized to be added here")}
                    </Typography>

                    <StyledWrapper {...cartWrapperStyle}>
                        <Zone contentId={id as never} zoneName={"TeamCards" as never} />
                    </StyledWrapper>
                </>
            )}

            {!isEditing && (
                <StyledWrapper {...cartWrapperStyle} ref={emblaRef}>
                    <Zone contentId={id as never} zoneName={"TeamCards" as never} fixed={true as never} />

                    {isMobile ? (
                        <StyledWrapper {...styles.dotsGroup}>
                            {scrollSnaps.map((item, index) => (
                                <button
                                    key={`team_group_dots__key-${item}`}
                                    className="dot"
                                    onClick={() => scrollTo(index)}
                                    disabled={index === selectedIndex}
                                ></button>
                            ))}
                        </StyledWrapper>
                    ) : null}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const definitionTab: TabDefinition = {
    displayName: "Definition",
    sortOrder: 1,
};

const stylesTab: TabDefinition = {
    displayName: "Styles",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(withIsInShell(TeamCardGroupWidget))) as any,
    definition: {
        displayName: "Team Card Group",
        group: "Verndale Box" as any,
        icon: "users",
        fieldDefinitions: [
            // Definition tab items:
            {
                tab: definitionTab,
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                tab: definitionTab,
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                tab: definitionTab,
                name: fields.maxNumber,
                displayName: fields.displayMaxNumber,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "3", value: 3 },
                    { displayName: "4", value: 4 },
                    { displayName: "5", value: 5 },
                    { displayName: "6", value: 6 },
                ],
                hideEmptyOption: true,
                defaultValue: 4,
                fieldType: "General",
                isRequired: true,
            },

            // Styles tab items:
            {
                tab: stylesTab,
                name: fields.spacingTop,
                displayName: fields.displaySpacingTop,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            {
                tab: stylesTab,
                name: fields.spacingBetweenCards,
                displayName: fields.displaySpacingBetweenCards,
                editorTemplate: "IntegerField",
                defaultValue: 20,
                fieldType: "General",
            },
            {
                tab: stylesTab,
                name: fields.spacingBottom,
                displayName: fields.displaySpacingBottom,
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
